import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTripBuilder } from '../TripBuilderContext';
import AnimatedCounter from './AnimatedCounter';

export default function FloatingBudget() {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const tb = useTripBuilder();

  // Don't show if nothing is selected yet
  if (!tb.destination && tb.itemCount === 0) return null;
  // Don't show desktop panel on PlanFlow (it has its own sidebar), but still show mobile bar
  const hiddenDesktop = location.pathname === '/';

  const amexPct = Math.min((tb.flightPoints / tb.amexTotal) * 100, 100);
  const chasePct = Math.min((tb.hotelPoints / tb.chaseTotal) * 100, 100);

  return (
    <>
      {/* Desktop floating panel */}
      <div className={`${hiddenDesktop ? 'hidden' : 'hidden md:block'} fixed bottom-24 right-6 z-40`}>
        <div
          className={`bg-navy-light/95 backdrop-blur-lg border border-white/15 rounded-2xl shadow-2xl shadow-black/40 transition-all duration-300 ${expanded ? 'w-80' : 'w-64'}`}
        >
          {/* Collapsed summary */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-left p-4 group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-teal animate-pulse" />
                <span className="text-[10px] font-bold text-brand-ink/65 uppercase tracking-wider">Live Trip Cost</span>
              </div>
              <span className="text-[10px] text-brand-ink/55 group-hover:text-brand-ink/70 transition">
                {expanded ? 'collapse' : 'expand'}
              </span>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="text-xl font-black text-teal">
                <AnimatedCounter value={tb.totalPoints} duration={600} /> <span className="text-xs font-medium text-brand-ink/65">pts</span>
              </div>
              {tb.totalCash > 0 && (
                <div className="text-sm font-bold text-gold">
                  +$<AnimatedCounter value={tb.totalCash} duration={600} />
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <MiniBar pct={amexPct} color="bg-blue-500" label={`${tb.amexRemaining.toLocaleString()} MR left`} />
              <MiniBar pct={chasePct} color="bg-indigo-500" label={`${tb.chaseRemaining.toLocaleString()} UR left`} />
            </div>
          </button>

          {/* Expanded detail */}
          {expanded && (
            <div className="px-4 pb-4 border-t border-brand-ink/10 pt-3 animate-fade-in space-y-3">
              {/* Dates */}
              {tb.startDate && tb.endDate && (
                <div className="text-xs text-brand-ink/70 bg-brand-ink/5 rounded-lg px-2.5 py-1.5">
                  {new Date(tb.startDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' - '}
                  {new Date(tb.endDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' '}&middot; <span className="text-teal font-medium">{tb.nights} nights</span>
                </div>
              )}

              {/* Trip items */}
              <div className="space-y-1.5">
                <BudgetRow
                  label="Destination"
                  value={tb.destination?.city}
                  empty="Not picked"
                />
                <BudgetRow
                  label="Flights"
                  value={tb.flight ? tb.flight.airline_program : null}
                  points={tb.flightPoints}
                  cash={tb.flightTaxes}
                  empty="Not picked"
                />
                <BudgetRow
                  label="Hotel"
                  value={tb.hotel ? tb.hotel.hotel_name : null}
                  points={tb.hotelPoints}
                  empty="Not picked"
                />
                <BudgetRow
                  label={`Activities (${tb.activities.length})`}
                  value={tb.activities.length > 0 ? `${tb.activities.length} planned` : null}
                  cash={tb.activityCash}
                  empty="None"
                />
                <BudgetRow
                  label={`Food (${tb.restaurants.length})`}
                  value={tb.restaurants.length > 0 ? `${tb.restaurants.length} spots` : null}
                  cash={tb.restaurantCash}
                  empty="None"
                />
              </div>

              {/* Totals */}
              <div className="border-t border-brand-ink/10 pt-2 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Amex MR remaining</span>
                  <span className={`font-semibold ${tb.amexRemaining < 0 ? 'text-danger' : 'text-blue-300'}`}>{tb.amexRemaining.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Chase UR remaining</span>
                  <span className={`font-semibold ${tb.chaseRemaining < 0 ? 'text-danger' : 'text-indigo-300'}`}>{tb.chaseRemaining.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-brand-ink/65">Total cash</span>
                  <span className="font-bold text-gold">${tb.totalCash.toLocaleString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Link
                  to="/"
                  className="flex-1 text-center py-2 bg-teal/20 text-teal text-xs font-bold rounded-lg hover:bg-teal/30 transition"
                >
                  Edit Trip
                </Link>
                {tb.mockups.length > 0 && (
                  <button
                    className="px-3 py-2 bg-brand-ink/5 text-brand-ink/70 text-xs rounded-lg hover:bg-brand-ink/10 transition"
                    title={`${tb.mockups.length} saved mockup(s)`}
                  >
                    {tb.mockups.length} saved
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile sticky bottom bar — anchored to very bottom, below chat bubble */}
      <div className="floating-budget-mobile md:hidden fixed bottom-0 left-0 right-0 z-[47] bg-navy-light/95 backdrop-blur-lg border-t border-white/15 px-4 py-2.5 pb-[env(safe-area-inset-bottom,8px)]">
        <Link to="/" className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-teal animate-pulse" />
            <div>
              <div className="text-xs text-brand-ink/65">Trip cost</div>
              <div className="text-sm font-bold text-teal">{tb.totalPoints.toLocaleString()} pts</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {tb.totalCash > 0 && (
              <div className="text-sm font-bold text-gold">+${tb.totalCash.toLocaleString()}</div>
            )}
            <div className="flex gap-1.5">
              <div className="text-[10px] text-blue-300">{tb.amexRemaining.toLocaleString()} MR</div>
              <div className="text-[10px] text-brand-ink/45">|</div>
              <div className="text-[10px] text-indigo-300">{tb.chaseRemaining.toLocaleString()} UR</div>
            </div>
          </div>
        </Link>
      </div>
    </>
  );
}

function MiniBar({ pct, color, label }) {
  return (
    <div className="flex-1">
      <div className="h-1.5 bg-brand-ink/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-brand-ink/55 mt-0.5">{label}</div>
    </div>
  );
}

function BudgetRow({ label, value, points, cash, empty }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2">
        <div className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-teal' : 'bg-brand-ink/20'}`} />
        <span className={`text-xs ${value ? 'text-brand-ink/80' : 'text-brand-ink/55'}`}>{label}</span>
      </div>
      <div className="text-right">
        {value ? (
          <div className="text-xs">
            {points > 0 && <span className="text-brand-ink/75">{points.toLocaleString()} pts</span>}
            {points > 0 && cash > 0 && <span className="text-brand-ink/45 mx-1">+</span>}
            {cash > 0 && <span className="text-gold">${cash.toLocaleString()}</span>}
            {!points && !cash && <span className="text-brand-ink/70">{value}</span>}
          </div>
        ) : (
          <span className="text-[10px] text-brand-ink/45">{empty}</span>
        )}
      </div>
    </div>
  );
}
